import React from "react";
import style from "./DebridementCommon.module.scss";
import { signedLockedAccess } from "../../../../../../utils";
import {
  BLEEDING_AMOUNT,
  DEBRIDEMENT_TYPES,
  DEPTH_OF_DEBRIDEMENT,
  INSTRUMENTS_USED,
  labelFor,
  labelsFor,
  NECROTIC_SUBCUTANEOUS_TISSUE,
  NON_TISSUE_REMOVED,
  NOT_PERFORMED_REASONS,
  TECHNIQUE_USED,
  YES_NO,
} from "../debridementConfig";
import { approachesFor } from "../debridementRules";
import { PERMISSIONS } from "../../../../../../constants/permissions";
import { Spinner } from "../../../../../secondary/Loader/Spinner";
import { Row } from "../../../../../secondary/Row/Row";

const ANSWER_LABEL = { yes: "Yes", no: "No", na: "N/A" };

export const DebridementViewMode = ({ resp, prerequisites, noDataMsg, onEdit, assessmentDetails, hasPermission, viewLoading }) => {
  if (viewLoading) {
    return <Spinner />;
  }
  if (noDataMsg || !resp) {
    return (
      <div className={style.form_container}>
        <div className="text-center py-5">
          <i className="fas fa-inbox fa-3x text-muted mb-3" />
          <p className="text-muted">No data available.</p>
        </div>
      </div>
    );
  }

  const performed = resp.isDebridementPerformed === "yes";
  const answers = {};
  (resp.prerequisites || []).forEach((p) => {
    answers[p.key] = p;
  });

  return (
    <div className={style.form_container}>
      {/* Block 0 */}
      <div className={style.form_actions}>
        <button
          type="button"
          className={style.form_edit_btn}
          onClick={onEdit}
          disabled={!hasPermission(PERMISSIONS.TREATMENT_EDIT) || !signedLockedAccess(assessmentDetails?.assessmentStage)}
        >
          <i className="fa fa-pencil" aria-hidden="true"></i>
          Edit
        </button>
      </div>
      <section className={style.form_section}>
        <div className={style.form_section_header}>
          <span className={style.form_section_title}>Was debridement performed?</span>
        </div>
        <div className={style.form_section_body}>
          <Row label="Debridement performed" value={performed ? "Yes" : "No"} />
          {!performed && <Row label="Reason not performed" value={labelFor(NOT_PERFORMED_REASONS, resp.notPerformedReason)} />}
        </div>
      </section>

      {/* Block 1 */}
      {performed && (
        <section className={style.form_section}>
          <div className={style.form_section_header}>
            <span className={style.form_section_title}>Prerequisites</span>
          </div>
          <div className={style.form_section_body}>
            {prerequisites.map((p) => {
              const a = answers[p.key];
              if (!a) return null;
              const selectionLabel = p.options ? labelFor(p.options, a.value) : a.value;
              return (
                <p className={style.form_view_row} key={p.key}>
                  <label>{p.label} :</label>
                  <span>
                    {ANSWER_LABEL[a.answer] || "—"}
                    {selectionLabel ? ` · ${selectionLabel}` : ""}
                    {a.answer === "no" && a.reasonText ? ` — ${a.reasonText}` : ""}
                  </span>
                </p>
              );
            })}
          </div>
        </section>
      )}

      {performed && (
        <>
          {/* Block 2 */}
          <section className={style.form_section}>
            <div className={style.form_section_header}>
              <span className={style.form_section_title}>Procedure</span>
            </div>
            <div className={style.form_section_body}>
              <Row label="Type of debridement" value={labelFor(DEBRIDEMENT_TYPES, resp.surgicalDebridementProcedure)} />
              <Row label="Approach" value={labelFor(approachesFor(resp.surgicalDebridementProcedure), resp.nonExcisionalApproach)} />
              <Row label="Deepest tissue removed" value={labelFor(DEPTH_OF_DEBRIDEMENT, resp.depthOfDebridement)} />
              <Row label="Subcutaneous tissue" value={labelsFor(NECROTIC_SUBCUTANEOUS_TISSUE, resp.necroticSubcutaneousTissue)} />
              <Row label="Other non-tissue types removed" value={labelsFor(NON_TISSUE_REMOVED, resp.necroticTissue)} />
              <Row label="Instruments used" value={labelsFor(INSTRUMENTS_USED, resp.instrumentsUsed)} />
              <Row label="Necrotic skin debrided (cm²)" value={resp.surfaceAreaOfNecroticSkinTissue} />
              <Row label="Necrotic subcutaneous tissue debrided (cm²)" value={resp.surfaceAreaOfNecroticSubqTissue} />
              <Row label="Technique used" value={labelFor(TECHNIQUE_USED, resp.techniqueUsed)} />
            </div>
          </section>

          {/* Block 3 */}
          <section className={style.form_section}>
            <div className={style.form_section_header}>
              <span className={style.form_section_title}>Outcome</span>
            </div>
            <div className={style.form_section_body}>
              <Row label="Pain right after procedure" value={resp.painAfter === null ? "" : String(resp.painAfter)} />
              <Row label="Bleeding" value={labelFor(BLEEDING_AMOUNT, resp.postDebridementBleedingAmount)} />
              <Row label="Pressure required to stop bleeding" value={labelFor(YES_NO, resp.stoppedBleedingContactPressure)} />
            </div>
          </section>
        </>
      )}
    </div>
  );
};
